/**
 * Live conversation updates over SSE (§4.3). `EventSource` can't send the
 * bearer header, so we read the `fetch` ReadableStream and parse SSE frames by
 * hand (same approach as the playground). A `message` event refreshes the
 * transcript; a `status` event refreshes the board.
 */

import { getToken } from "@cometchat-ai/auth/client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { readBaseUrl } from "../agents/api/client";
import { conversationKeys } from "../agents/api/conversations";
import { sessionKeys } from "../agents/api/sessions";
import { liveKeys } from "./api";

export interface SessionStreamEvent {
  type: string;
  sessionId?: string;
  status?: string;
}

/**
 * Read an SSE stream via `fetch` (EventSource can't send the bearer header),
 * parsing `\n\n`-delimited frames and forwarding each `data:` payload. Resolves
 * when the stream ends or the signal aborts; never throws.
 */
async function readSseStream(
  path: string,
  onEvent: (event: SessionStreamEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  try {
    const res = await fetch(`${readBaseUrl()}${path}`, {
      headers: { Authorization: `Bearer ${getToken() ?? ""}`, Accept: "text/event-stream" },
      signal,
    });
    if (!res.ok || !res.body) return;
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
        if (!dataLine) continue;
        try {
          onEvent(JSON.parse(dataLine.slice(5).trim()) as SessionStreamEvent);
        } catch {
          // ignore a malformed frame
        }
      }
    }
  } catch {
    // aborted (unmount / dep change) or a network blip — reconnects on re-run.
  }
}

export function useSessionStream(
  sessionId: string | undefined,
  onEvent: (event: SessionStreamEvent) => void,
): void {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!sessionId) return;
    const controller = new AbortController();
    void readSseStream(
      `/api/sessions/${sessionId}/stream`,
      (e) => onEventRef.current(e),
      controller.signal,
    );
    return () => controller.abort();
  }, [sessionId]);
}

/** Workspace-wide handoff-board stream — any status change nudges a refetch. */
export function useHandoffBoardStream(onEvent: (event: SessionStreamEvent) => void): void {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  useEffect(() => {
    const controller = new AbortController();
    void readSseStream("/api/handoffs/stream", (e) => onEventRef.current(e), controller.signal);
    return () => controller.abort();
  }, []);
}

/**
 * Bind the live board to the SSE stream: any status/message/resume event in the
 * workspace invalidates the board query so the queue updates without polling.
 */
export function useHandoffBoardLiveSync(slug: string): void {
  const queryClient = useQueryClient();
  const onEvent = useCallback(
    (event: SessionStreamEvent) => {
      if (event.type === "status" || event.type === "message" || event.type === "resume") {
        void queryClient.invalidateQueries({ queryKey: liveKeys.list(slug) });
      }
    },
    [queryClient, slug],
  );
  useHandoffBoardStream(onEvent);
}

/**
 * Bind a conversation's live stream to the react-query cache: `message` →
 * refresh the transcript, `status` → refresh the conversation list/board.
 */
export function useConversationLiveSync(sessionId: string): void {
  const queryClient = useQueryClient();
  const onEvent = useCallback(
    (event: SessionStreamEvent) => {
      if (event.type === "message") {
        void queryClient.invalidateQueries({ queryKey: sessionKeys.messages(sessionId) });
      } else if (event.type === "status") {
        void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
      }
    },
    [queryClient, sessionId],
  );
  useSessionStream(sessionId, onEvent);
}
